import { type FC, useEffect } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Stack,
  Typography,
} from "@mui/material";
import TaskRoundedIcon from "@mui/icons-material/TaskRounded";
import { Link as RouterLink } from "react-router-dom";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { DialogHeader, ErrorMessage } from "@/shared/components";
import {
  useDownloadReportMutation,
  useReportQuery,
} from "@/partner/entities/reports";
import { PagePaths } from "@/partner/shared/constants";
import { ReportStatusType } from "@/types/graphql/partner.generated";
import { reportErrorKey } from "../model/reportLabels";

const POLL_INTERVAL_MS = 3000;

interface ReportProgressDialogProps {
  reportId: string;
  onClose: () => void;
}

export const ReportProgressDialog: FC<ReportProgressDialogProps> = ({
  reportId,
  onClose,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { data, error, startPolling, stopPolling } = useReportQuery({
    variables: { id: reportId },
    fetchPolicy: "network-only",
  });
  const report = data?.report;
  const active =
    !report ||
    report.status === ReportStatusType.PendingType ||
    report.status === ReportStatusType.ProcessingType;

  // Скрытая вкладка пропускает тики через глобальный skipPollAttempt (apollo.ts).
  useEffect(() => {
    if (active) startPolling(POLL_INTERVAL_MS);
    else stopPolling();
    return () => stopPolling();
  }, [active, startPolling, stopPolling]);

  const [downloadReport, { loading: downloading }] = useDownloadReportMutation({
    variables: { id: reportId },
    onCompleted: (result) => {
      // Не window.open: после await мутации блокировщик всплывающих окон
      // режет попап. XLSX отдаётся на скачивание, страница остаётся.
      window.location.assign(result.downloadReport.url);
    },
    onError: (err) => enqueueSnackbar(err.message, { variant: "error" }),
  });

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogHeader
        title={t("reports.dialog.title")}
        onClose={onClose}
        loading={active}
      />
      <DialogContent>
        {error && <ErrorMessage error={error} />}
        {active && (
          <Stack alignItems="center" spacing={2} sx={{ py: 2 }}>
            <CircularProgress
              size={56}
              variant={report?.progress ? "determinate" : "indeterminate"}
              value={report?.progress ?? 0}
            />
            {report?.progress ? (
              <Typography variant="h6" color="textSecondary">
                {Math.round(report.progress)}%
              </Typography>
            ) : null}
            <Typography align="center" color="textSecondary">
              {t("reports.dialog.inProgress")}
            </Typography>
          </Stack>
        )}
        {report?.status === ReportStatusType.ErrorType && (
          <ErrorMessage
            error={{ message: t(reportErrorKey(report.error?.code)) }}
          />
        )}
        {report?.status === ReportStatusType.CompletedType && (
          <Stack alignItems="center" spacing={2} sx={{ py: 2 }}>
            <TaskRoundedIcon sx={{ fontSize: 48 }} />
            <Typography variant="h6">{t("reports.dialog.done")}</Typography>
            <Box>
              <Button
                variant="contained"
                disableElevation
                size="large"
                disabled={downloading}
                onClick={() => downloadReport()}
              >
                {t("reports.dialog.download")}
              </Button>
            </Box>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          component={RouterLink}
          to={PagePaths.reports}
          color="inherit"
          onClick={onClose}
        >
          {t("reports.dialog.allReports")}
        </Button>
        <Button onClick={onClose} variant="outlined" color="inherit">
          {t("reports.dialog.close")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
